import React, { useState, useEffect } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface UniversalWaitingRoomProps {
  sessionId: string;
  onRedirect?: (redirectUrl: string) => void;
}

interface ExternalSessionStatus {
  session_id: string;
  status: 'waiting' | 'accepted' | 'active' | 'completed' | 'expired' | 'cancelled';
  created_at: string;
  template_type: 'legal' | 'support' | 'homeimprovement';
  request_id: string;
  session_url: string;
  participant_data: {
    name: string;
    email: string;
    phone?: string;
  };
  host_data: {
    name: string;
    company: string;
    host_id?: string;
  };
  estimated_wait_time?: string;
  expires_at: string;
}

export const UniversalWaitingRoom: React.FC<UniversalWaitingRoomProps> = ({
  sessionId,
  onRedirect
}) => {
  const [sessionData, setSessionData] = useState<ExternalSessionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [waitTime, setWaitTime] = useState(0);

  // Fetch session status
  const fetchSessionStatus = async () => {
    try {
      // In a real implementation, this would use the actual API endpoint
      // For now, we'll simulate the response structure
      console.log('Fetching session status for:', sessionId);
      
      // This would be the actual API call:
      // const response = await fetch(`/api/v1/external/session-status/${sessionId}`);
      // const data = await response.json();
      
      // Simulated response for development
      const mockData: ExternalSessionStatus = {
        session_id: sessionId,
        status: 'waiting',
        created_at: new Date().toISOString(),
        template_type: 'homeimprovement', // This would come from the actual session
        request_id: 'proj_12345',
        session_url: `https://customerconnects.app/external/${sessionId}`,
        participant_data: {
          name: 'John Customer',
          email: 'john@example.com',
          phone: '+1234567890'
        },
        host_data: {
          name: 'Jane Contractor',
          company: 'Home Improvement Co',
          host_id: 'contractor_001'
        },
        estimated_wait_time: '5-10 minutes',
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      };
      
      setSessionData(mockData);
      setLoading(false);
      
      // Check if we should redirect to template-specific waiting room
      if (mockData.status === 'waiting' && onRedirect) {
        const redirectUrl = getTemplateWaitingRoomUrl(mockData.template_type, mockData.request_id, sessionId);
        onRedirect(redirectUrl);
      }
      
    } catch (err) {
      console.error('Error fetching session status:', err);
      setError('Unable to load session information');
      setLoading(false);
    }
  };

  // Generate template-specific waiting room URL
  const getTemplateWaitingRoomUrl = (templateType: string, requestId: string, sessionId: string): string => {
    switch (templateType) {
      case 'legal':
        return `/templates/legal/waiting/${requestId}?sessionId=${sessionId}`;
      case 'support':
        return `/templates/support/waiting/${requestId}?sessionId=${sessionId}`;
      case 'homeimprovement':
        return `/templates/homeimprovement/waiting/${requestId}?sessionId=${sessionId}`;
      default:
        return `/templates/legal/waiting/${requestId}?sessionId=${sessionId}`;
    }
  };

  // Get template-specific branding
  const getTemplateBranding = (templateType: string) => {
    switch (templateType) {
      case 'legal':
        return {
          primaryColor: 'blue',
          icon: '⚖️',
          title: 'Legal Consultation',
          description: 'Your attorney will join shortly'
        };
      case 'support':
        return {
          primaryColor: 'green',
          icon: '🎧',
          title: 'Support Session',
          description: 'A support agent will assist you soon'
        };
      case 'homeimprovement':
        return {
          primaryColor: 'orange',
          icon: '🏠',
          title: 'Home Consultation',
          description: 'Your contractor will be with you shortly'
        };
      default:
        return {
          primaryColor: 'blue',
          icon: '💬',
          title: 'Video Consultation',
          description: 'Please wait to be connected'
        };
    }
  };

  useEffect(() => {
    fetchSessionStatus();
    
    // Poll for status updates every 5 seconds
    const interval = setInterval(fetchSessionStatus, 5000);
    
    // Update wait time every second
    const waitTimeInterval = setInterval(() => {
      setWaitTime(prev => prev + 1);
    }, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(waitTimeInterval);
    };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Session Not Found</h1>
          <p className="text-gray-600 mb-4">
            {error || 'This session may have expired or is invalid.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const branding = getTemplateBranding(sessionData.template_type);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">{branding.icon}</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {branding.title}
          </h1>
          <p className="text-lg text-gray-600">
            {branding.description}
          </p>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-3 ${
                sessionData.status === 'waiting' ? 'bg-yellow-500 animate-pulse' :
                sessionData.status === 'accepted' ? 'bg-blue-500' :
                sessionData.status === 'active' ? 'bg-green-500' :
                'bg-gray-500'
              }`}></div>
              <span className="font-semibold text-gray-900 capitalize">
                {sessionData.status === 'waiting' ? 'Waiting for host' :
                 sessionData.status === 'accepted' ? 'Connecting...' :
                 sessionData.status === 'active' ? 'Session active' :
                 sessionData.status}
              </span>
            </div>
            
            {sessionData.status === 'waiting' && (
              <div className="flex items-center text-gray-600">
                <Clock className="h-4 w-4 mr-1" />
                <span className="text-sm">{formatTime(waitTime)}</span>
              </div>
            )}
          </div>

          {/* Progress indicator */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Request submitted</span>
              <span>Host notified</span>
              <span>Connected</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className={`h-2 rounded-full transition-all duration-500 ${
                sessionData.status === 'waiting' ? 'bg-yellow-500 w-1/3' :
                sessionData.status === 'accepted' ? 'bg-blue-500 w-2/3' :
                sessionData.status === 'active' ? 'bg-green-500 w-full' :
                'bg-gray-400 w-1/3'
              }`}></div>
            </div>
          </div>

          {/* Wait time estimate */}
          {sessionData.status === 'waiting' && sessionData.estimated_wait_time && (
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-sm text-blue-800">
                <Users className="h-4 w-4 inline mr-1" />
                Estimated wait time: <strong>{sessionData.estimated_wait_time}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Session Details */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Session Details</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Participant:</span>
              <span className="font-medium">{sessionData.participant_data.name}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Host:</span>
              <span className="font-medium">{sessionData.host_data.name}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Company:</span>
              <span className="font-medium">{sessionData.host_data.company}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Session ID:</span>
              <span className="font-mono text-sm">{sessionData.session_id}</span>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
          
          <div className="space-y-3">
            <div className="flex items-start">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900">Your request has been submitted</p>
                <p className="text-sm text-gray-600">
                  {sessionData.host_data.name} at {sessionData.host_data.company} has been notified
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className={`h-5 w-5 rounded-full mt-0.5 mr-3 flex-shrink-0 ${
                sessionData.status !== 'waiting' ? 'bg-green-500 text-white flex items-center justify-center' : 'bg-gray-200'
              }`}>
                {sessionData.status !== 'waiting' && <CheckCircle className="h-3 w-3" />}
                {sessionData.status === 'waiting' && <span className="text-xs font-bold text-gray-600">2</span>}
              </div>
              <div>
                <p className="font-medium text-gray-900">Host will join the session</p>
                <p className="text-sm text-gray-600">
                  You'll be automatically connected when they're ready
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className={`h-5 w-5 rounded-full mt-0.5 mr-3 flex-shrink-0 ${
                sessionData.status === 'active' ? 'bg-green-500 text-white flex items-center justify-center' : 'bg-gray-200'
              }`}>
                {sessionData.status === 'active' && <CheckCircle className="h-3 w-3" />}
                {sessionData.status !== 'active' && <span className="text-xs font-bold text-gray-600">3</span>}
              </div>
              <div>
                <p className="font-medium text-gray-900">Start your consultation</p>
                <p className="text-sm text-gray-600">
                  Your video session will begin automatically
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Support */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-600">
            Having issues? Contact support at{' '}
            <a href="mailto:support@customerconnects.app" className="text-blue-600 hover:text-blue-700">
              support@customerconnects.app
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};